import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import { Flex } from '../../layout/flex'
import { SG, PB, SLB, SLR, SY, WHITE } from '../../colors'

const heights = {
  small: '10px',
  large: '15px'
}
const colors = {
  green: SG,
  red: SLR,
  blue: SLB,
  monochrome: PB
}
const getStackedStyles = ({ zIndex }) => {
  if (!zIndex) {
    return null
  }

  return css`
    &:nth-child(n + 2) {
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    }
  `
}
const Container = styled(Flex)`
  background-color: ${({ bgColor }) => bgColor};
  border-radius: 25px;
  position: relative;
`
const Hash = styled.div`
  height: ${({ size }) => heights[size]};
  position: absolute;
  border-left: dotted 2.5px ${SY};
  left: ${({ hashPosition }) => hashPosition}%;
  z-index: ${({ zIndex }) => zIndex};
`
const Bar = styled.div`
  visibility: ${({ value }) => (value ? '' : 'hidden')};
  box-sizing: border-box;
  border-radius: 25px;
  outline: 2px solid ${WHITE};
  width: ${({ value }) => value}%;
  height: ${({ height }) => height};
  background-color: ${({ coloring }) => coloring};
  z-index: ${({ zIndex }) => zIndex};
  transition: width 0.2s ease-in-out;
  ${getStackedStyles};

  :first-of-type {
    width: ${({ value }) => value}%;
    margin-left: 0;
  }
`
const renderBar = (value, display, size, color) => {
  if (Array.isArray(value)) {
    return value.map((bar, index) => {
      return (
        <Bar
          value={bar.value}
          coloring={colors[bar.color] ?? bar.color}
          height={heights[size]}
          key={bar.color}
          zIndex={value.length - index}
        />
      )
    })
  }

  return <Bar value={value} coloring={color ?? colors[display]} height={heights[size]} />
}
/**
 * The ProgressBar component can be used to display the length or percent completed of a determinate process.
 */
const ProgressBar = ({
  color,
  size = 'small',
  display = 'green',
  value = 0,
  bgColor = WHITE,
  hashPosition = 0
}) => {
  const isMultipleValue = Array.isArray(value)

  return (
    <Container bgColor={bgColor}>
      {renderBar(value, display, size, color)}
      {hashPosition && hashPosition < 100 ? (
        <Hash
          size={size}
          zIndex={isMultipleValue ? value.length : 0}
          hashPosition={hashPosition}
        />
      ) : null}
    </Container>
  )
}

ProgressBar.propTypes = {
  /** Sets the thickness of the progress bar. */
  size: PropTypes.oneOf(['small', 'large']),
  /** Sets the color of the progress bar. */
  display: PropTypes.oneOf(['green', 'red', 'blue', 'monochrome']),
  /** Sets the position of the hash */
  hashPosition: PropTypes.number,
  /** Sets the color of the progress bar to any value, overriding display. */
  color: PropTypes.string,
  /** Sets the background color of the progress bar. */
  bgColor: PropTypes.string,
  /** The value of the progress indicator. A number between 0 and 100. */
  value(props, propName, componentName) {
    const value = props[propName]
    const errMsgBase = `Invalid prop \`${propName}\` supplied to \`${componentName}\`.`
    const entries = Array.isArray(value) ? value : [{ value }]

    for (const entry of entries) {
      if (!Number.isInteger(entry.value)) {
        return new Error(`${errMsgBase} Integer expected.`)
      }

      if (entry.value < 0 || entry.value > 100) {
        return new Error(`${errMsgBase} Must be between 0 and 100.`)
      }
    }
  }
}

export { ProgressBar }
